import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

interface BreadcrumbItem {
  label: string;
  path: string;
}

const Breadcrumbs = () => {
  const location = useLocation();

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const paths = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Inicio', path: '/' }
    ];

    let currentPath = '';
    paths.forEach((path) => {
      currentPath += `/${path}`;
      
      // Mapeo de rutas a nombres legibles
      const pathLabels: Record<string, string> = {
        'servicios': 'Servicios',
        'arquitectura-semantica': 'Arquitectura Semántica',
        'schema-markup': 'Schema Markup',
        'seo-agentes-ia': 'SEO para Agentes IA',
        'voice-search-seo': 'Voice Search SEO',
        'progressive-web-app': 'Progressive Web App',
        'optimizacion-extrema': 'Optimización Extrema',
        'politica-privacidad': 'Política de Privacidad',
        'politica-cookies': 'Política de Cookies',
        'terminos-condiciones': 'Términos y Condiciones'
      };

      const label = pathLabels[path] || path.charAt(0).toUpperCase() + path.slice(1);
      breadcrumbs.push({ label, path: currentPath });
    });

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  // Generar Schema.org BreadcrumbList
  useEffect(() => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.label,
        "item": `https://seogenia.es${crumb.path}`
      }))
    };

    // Eliminar schema anterior si existe
    const existingScript = document.getElementById('breadcrumb-schema');
    if (existingScript) {
      existingScript.remove();
    }

    // Añadir nuevo schema
    const script = document.createElement('script');
    script.id = 'breadcrumb-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('breadcrumb-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [location.pathname]);

  // No mostrar breadcrumbs en la home
  if (location.pathname === '/') {
    return null;
  }

  return (
    <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <ol className="flex items-center space-x-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
          {breadcrumbs.map((crumb, index) => (
            <li
              key={crumb.path}
              className="flex items-center"
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              {index > 0 && (
                <i className="ri-arrow-right-s-line text-gray-400 mx-2"></i>
              )}
              {index === breadcrumbs.length - 1 ? (
                <span
                  className="text-gray-900 font-medium"
                  itemProp="name"
                  aria-current="page"
                >
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  className="text-gray-600 hover:text-cyan-600 transition-colors duration-200"
                  itemProp="item"
                >
                  <span itemProp="name">{crumb.label}</span>
                </Link>
              )}
              <meta itemProp="position" content={String(index + 1)} />
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;
